'use client';

import { useMemo, type ReactNode } from 'react';
import {
  InternalNetworkConnectionContent,
  resolveSensorReleaseDisplay,
  sortByCreatedAtDesc,
  type InternalNetworkOutboundConnectivitySectionProps,
  type InternalNetworkSensorEnrollmentsPanelProps,
  type InternalNetworkTunnelEnrollmentsPanelProps,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  useCreateInternalNetworkEnrollment,
  useCreateInternalNetworkTunnelEnrollment,
  useInternalNetworkDeploymentRequirements,
  useInternalNetworkTunnelEnrollments,
  useLatestInternalNetworkSensorRelease,
  useReprovisionInternalNetworkTunnelEnrollment,
  useRevokeInternalNetworkEnrollment,
  useRevokeInternalNetworkTunnelEnrollment,
  useRotateInternalNetworkEnrollmentKey,
} from '../../../../hooks/api/useInternalNetworkConnection';
import { useInternalNetworkEnrollments } from '../../../../hooks/api/useSurfaceSettings';
import { useInternalNetworkSSE } from '../../../../hooks/useInternalNetworkSSE';
import { InternalNetworkSensorConnectionProgress } from './InternalNetworkSensorConnectionProgress';

type InternalSensorSettingsPanelProps = {
  networkId: string;
  isTunnelModeEnabled: boolean;
  isReadOnly: boolean;
};

type ConnectionContentBaseProps = {
  sensorEnrollments: InternalNetworkSensorEnrollmentsPanelProps;
  outboundConnectivity: InternalNetworkOutboundConnectivitySectionProps;
  sensorConnectionProgress: ReactNode;
};

function useTunnelEnrollmentsPanelProps({
  networkId,
  isReadOnly,
}: {
  networkId: string;
  isReadOnly: boolean;
}): InternalNetworkTunnelEnrollmentsPanelProps {
  const tunnelEnrollmentsQuery = useInternalNetworkTunnelEnrollments({
    networkId,
  });
  const createTunnelEnrollment =
    useCreateInternalNetworkTunnelEnrollment(networkId);
  const reprovisionTunnelEnrollment =
    useReprovisionInternalNetworkTunnelEnrollment(networkId);
  const revokeTunnelEnrollment =
    useRevokeInternalNetworkTunnelEnrollment(networkId);
  const sortedTunnelEnrollments = useMemo(
    () => sortByCreatedAtDesc(tunnelEnrollmentsQuery.data?.data),
    [tunnelEnrollmentsQuery.data],
  );

  return {
    tunnelEnrollments: sortedTunnelEnrollments,
    isLoading: tunnelEnrollmentsQuery.isLoading,
    isReadOnly,
    onCreateTunnelEnrollment: (payload) =>
      createTunnelEnrollment.mutateAsync(payload),
    onReprovisionTunnelEnrollment: (enrollmentId) =>
      reprovisionTunnelEnrollment.mutateAsync(enrollmentId),
    onRevokeTunnelEnrollment: (enrollmentId) =>
      revokeTunnelEnrollment.mutateAsync(enrollmentId),
  };
}

function TunnelModeConnectionContent({
  networkId,
  isReadOnly,
  ...baseProps
}: ConnectionContentBaseProps & { networkId: string; isReadOnly: boolean }) {
  const tunnelEnrollments = useTunnelEnrollmentsPanelProps({
    networkId,
    isReadOnly,
  });

  return (
    <InternalNetworkConnectionContent
      {...baseProps}
      isTunnelModeEnabled
      tunnelEnrollments={tunnelEnrollments}
    />
  );
}

export function InternalSensorSettingsPanel({
  networkId,
  isTunnelModeEnabled,
  isReadOnly,
}: InternalSensorSettingsPanelProps) {
  useInternalNetworkSSE({ networkId });
  const tDeployment = useTranslations(
    'designSystem.networkSettings.internalConnection.deployment',
  );
  const enrollmentsQuery = useInternalNetworkEnrollments(networkId);
  const deploymentRequirementsQuery =
    useInternalNetworkDeploymentRequirements();
  const sensorReleaseQuery = useLatestInternalNetworkSensorRelease();
  const createEnrollment = useCreateInternalNetworkEnrollment(networkId);
  const revokeEnrollment = useRevokeInternalNetworkEnrollment(networkId);
  const rotateEnrollmentKey = useRotateInternalNetworkEnrollmentKey(networkId);
  const sortedEnrollments = useMemo(
    () => sortByCreatedAtDesc(enrollmentsQuery.data?.data),
    [enrollmentsQuery.data],
  );
  const sensorRelease = useMemo(
    () =>
      resolveSensorReleaseDisplay({
        latestRelease: sensorReleaseQuery.data,
        deploymentRequirements: deploymentRequirementsQuery.data,
        defaultVersionLabel: tDeployment('sensorReleaseDefault'),
      }),
    [deploymentRequirementsQuery.data, sensorReleaseQuery.data, tDeployment],
  );
  const connectionProgress = (
    <InternalNetworkSensorConnectionProgress networkId={networkId} />
  );
  const baseProps: ConnectionContentBaseProps = {
    sensorEnrollments: {
      enrollments: sortedEnrollments,
      isLoading: enrollmentsQuery.isLoading,
      isReadOnly,
      onCreateEnrollment: () => createEnrollment.mutateAsync(),
      onRotateEnrollmentKey: (enrollmentId) =>
        rotateEnrollmentKey.mutateAsync(enrollmentId),
      onRevokeEnrollment: (enrollmentId) =>
        revokeEnrollment.mutateAsync(enrollmentId),
      secretsDialogConnectionProgress: connectionProgress,
    },
    outboundConnectivity: {
      outboundEndpoints:
        deploymentRequirementsQuery.data?.outboundEndpoints ?? [],
      sensorRelease,
      isSensorReleaseLoading: sensorReleaseQuery.isLoading,
    },
    sensorConnectionProgress: connectionProgress,
  };

  return (
    <VStack gap={8} alignItems="stretch" px={8} pb={8} w="full">
      {isTunnelModeEnabled ? (
        <TunnelModeConnectionContent
          networkId={networkId}
          isReadOnly={isReadOnly}
          {...baseProps}
        />
      ) : (
        <InternalNetworkConnectionContent
          {...baseProps}
          isTunnelModeEnabled={false}
        />
      )}
    </VStack>
  );
}
